<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo / Brand -->
          <div class="flex items-center space-x-3">
            <router-link to="/dashboard" class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <font-awesome-icon icon="file-signature" class="text-white text-lg" />
              </div>
              <div>
                <h1 class="text-lg font-semibold text-gray-900">Closing Portal</h1>
                <p v-if="settlementAgentName" class="text-xs text-gray-500">{{ settlementAgentName }}</p>
              </div>
            </router-link>
          </div>

          <!-- Loan Info Summary -->
          <div class="hidden md:flex items-center space-x-6 text-sm">
            <div v-if="loan?.id" class="text-right">
              <p class="text-xs text-gray-500">Loan #</p>
              <p class="font-mono font-medium text-gray-900">{{ loan.id }}</p>
            </div>
            <div v-if="loan?.borrower_names" class="text-right">
              <p class="text-xs text-gray-500">Borrower</p>
              <p class="font-medium text-gray-900">{{ truncateName(loan.borrower_names) }}</p>
            </div>
            <div v-if="loan?.property" class="text-right">
              <p class="text-xs text-gray-500">Property</p>
              <p class="font-medium text-gray-900">{{ loan.property.address_line_1 }}</p>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Progress Tracker Bar -->
    <div class="bg-white border-b border-gray-100">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <!-- Steps -->
          <div class="flex items-center flex-1">
            <!-- Step 1 -->
            <router-link to="/title-info" class="flex items-center group">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
                :class="getStepClass(1)"
              >
                <font-awesome-icon v-if="closingProgress.titleInfoComplete" icon="check" />
                <span v-else>1</span>
              </div>
              <span class="ml-2 text-sm font-medium hidden sm:inline" :class="getStepTextClass(1)">Title</span>
            </router-link>

            <div class="h-1 w-8 sm:w-12 mx-2 sm:mx-3 rounded" :class="getConnectorClass(1)"></div>

            <!-- Step 2 -->
            <router-link to="/fees" class="flex items-center group">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
                :class="getStepClass(2)"
              >
                <font-awesome-icon v-if="closingProgress.feesComplete" icon="check" />
                <span v-else>2</span>
              </div>
              <span class="ml-2 text-sm font-medium hidden sm:inline" :class="getStepTextClass(2)">Fees</span>
            </router-link>

            <div class="h-1 w-8 sm:w-12 mx-2 sm:mx-3 rounded" :class="getConnectorClass(2)"></div>

            <!-- Step 3 -->
            <router-link to="/payoffs" class="flex items-center group">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
                :class="getStepClass(3)"
              >
                <font-awesome-icon v-if="closingProgress.payoffsComplete" icon="check" />
                <span v-else>3</span>
              </div>
              <span class="ml-2 text-sm font-medium hidden sm:inline" :class="getStepTextClass(3)">Payoffs</span>
            </router-link>

            <div class="h-1 w-8 sm:w-12 mx-2 sm:mx-3 rounded" :class="getConnectorClass(3)"></div>

            <!-- Step 4 -->
            <router-link to="/preview" class="flex items-center group">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
                :class="getStepClass(4)"
              >
                <font-awesome-icon v-if="closingProgress.previewGenerated" icon="check" />
                <span v-else>4</span>
              </div>
              <span class="ml-2 text-sm font-medium hidden sm:inline" :class="getStepTextClass(4)">Preview</span>
            </router-link>

            <div class="h-1 w-8 sm:w-12 mx-2 sm:mx-3 rounded" :class="getConnectorClass(4)"></div>

            <!-- Step 5 -->
            <router-link to="/documents" class="flex items-center group">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
                :class="getStepClass(5)"
              >
                <font-awesome-icon v-if="closingProgress.documentsGenerated" icon="check" />
                <span v-else>5</span>
              </div>
              <span class="ml-2 text-sm font-medium hidden sm:inline" :class="getStepTextClass(5)">Generate</span>
            </router-link>
          </div>

          <!-- Progress % -->
          <div class="ml-6 text-right">
            <span class="text-2xl font-bold text-primary-600">{{ overallProgress }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Borrower/Property Info -->
    <div class="md:hidden bg-gray-100 border-b border-gray-200">
      <div class="max-w-5xl mx-auto px-4 py-3">
        <div class="flex items-center justify-between text-sm">
          <div v-if="loan?.id">
            <span class="text-gray-500">Loan #</span>
            <span class="font-mono font-medium text-gray-900 ml-1">{{ loan.id }}</span>
          </div>
          <div v-if="loan?.borrower_names">
            <span class="text-gray-500">Borrower:</span>
            <span class="font-medium text-gray-900 ml-1">{{ truncateName(loan.borrower_names) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Tolerance Warning Banner -->
    <div v-if="hasToleranceWarning && !hasToleranceViolation" class="tolerance-warning px-4 py-3">
      <div class="max-w-5xl mx-auto flex items-center">
        <font-awesome-icon icon="exclamation-triangle" class="text-warning-600 mr-3" />
        <span class="text-sm font-medium">
          Warning: Fees are approaching tolerance limits. Review before generating documents.
        </span>
      </div>
    </div>

    <!-- Tolerance Violation Banner -->
    <div v-if="hasToleranceViolation" class="tolerance-exceeded px-4 py-3">
      <div class="max-w-5xl mx-auto flex items-center">
        <font-awesome-icon icon="exclamation-circle" class="text-danger-600 mr-3" />
        <span class="text-sm font-medium">
          Tolerance Exceeded: Fee amounts exceed disclosed limits. Document generation is blocked until resolved.
        </span>
      </div>
    </div>

    <!-- Main Content -->
    <main class="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
      <slot />
    </main>

    <!-- Loan Officer Contact Bar -->
    <div v-if="loanOfficer" class="bg-primary-50 border-t border-primary-100">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="flex items-center">
            <div class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
              <font-awesome-icon icon="user-tie" class="text-primary-600" />
            </div>
            <div>
              <p class="text-xs text-primary-600 font-medium">Loan Officer</p>
              <p class="font-semibold text-gray-900">{{ loanOfficer.name }}</p>
            </div>
          </div>
          <div class="flex items-center space-x-4 sm:space-x-6 text-sm">
            <a 
              v-if="loanOfficer.email" 
              :href="`mailto:${loanOfficer.email}`" 
              class="flex items-center text-primary-600 hover:text-primary-700 transition-colors"
            >
              <font-awesome-icon icon="envelope" class="mr-2" />
              <span class="hidden sm:inline">{{ loanOfficer.email }}</span>
              <span class="sm:hidden">Email</span>
            </a>
            <a 
              v-if="loanOfficer.phone" 
              :href="`tel:${loanOfficer.phone}`" 
              class="flex items-center text-primary-600 hover:text-primary-700 transition-colors"
            >
              <font-awesome-icon icon="phone" class="mr-2" />
              <span>{{ loanOfficer.phone }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="bg-white border-t border-gray-100">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="text-center text-sm text-gray-500">
          <p>&copy; {{ currentYear }} ReversePilot. All rights reserved.</p>
        </div>
      </div>
    </footer>

    <!-- Loading Overlay -->
    <Loading v-if="isLoading" :message="loadingMessage" />
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import Loading from '@/components/Loading/index.vue'

export default {
  name: 'TitleLayout',
  components: {
    Loading,
  },
  setup() {
    const store = useStore()

    const isLoading = computed(() => store.getters.isLoading)
    const loadingMessage = computed(() => store.getters.loadingMessage)
    const settlementAgent = computed(() => store.getters.settlementAgent)
    const loan = computed(() => store.getters.loan)
    const loanOfficer = computed(() => store.getters.loanOfficer)
    const toleranceStatus = computed(() => store.getters.toleranceStatus)
    const closingProgress = computed(() => store.getters.closingProgress)

    const settlementAgentName = computed(() => {
      if (settlementAgent.value) {
        return settlementAgent.value.name
      }
      return null
    })

    const hasToleranceWarning = computed(() => store.getters.hasToleranceWarning)
    const hasToleranceViolation = computed(() => store.getters.hasToleranceViolation)
    const overallProgress = computed(() => store.getters.overallProgress)

    const currentYear = new Date().getFullYear()

    const truncateName = (name) => {
      if (!name) return ''
      if (name.length > 25) {
        return name.substring(0, 22) + '...'
      }
      return name
    }

    const isStepComplete = (step) => {
      switch (step) {
        case 1: return closingProgress.value.titleInfoComplete
        case 2: return closingProgress.value.feesComplete
        case 3: return closingProgress.value.payoffsComplete
        case 4: return closingProgress.value.previewGenerated
        case 5: return closingProgress.value.documentsGenerated
        default: return false
      }
    }

    const getCurrentStep = () => {
      if (!closingProgress.value.titleInfoComplete) return 1
      if (!closingProgress.value.feesComplete) return 2
      if (!closingProgress.value.payoffsComplete) return 3
      if (!closingProgress.value.previewGenerated) return 4
      if (!closingProgress.value.documentsGenerated) return 5
      return 6
    }

    const getStepClass = (step) => {
      if (isStepComplete(step)) {
        return 'bg-primary-600 text-white'
      }
      if (step === getCurrentStep()) {
        return 'bg-primary-100 text-primary-700 ring-2 ring-primary-600'
      }
      return 'bg-gray-200 text-gray-500'
    }

    const getStepTextClass = (step) => {
      if (isStepComplete(step)) {
        return 'text-primary-600'
      }
      if (step === getCurrentStep()) {
        return 'text-primary-700'
      }
      return 'text-gray-500'
    }

    const getConnectorClass = (step) => {
      if (isStepComplete(step)) {
        return 'bg-primary-600'
      }
      return 'bg-gray-200'
    }

    return {
      isLoading,
      loadingMessage,
      settlementAgentName,
      loan,
      loanOfficer,
      toleranceStatus,
      closingProgress,
      hasToleranceWarning,
      hasToleranceViolation,
      overallProgress,
      currentYear,
      truncateName,
      getStepClass,
      getStepTextClass,
      getConnectorClass,
    }
  },
}
</script>
